import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	CircularProgress,
	Tabs,
	Tab,
	Card,
	CardContent,
	Chip,
} from "@mui/material";
import { BasicModal } from "../BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../PurchaseChoiceModal";
import { useFirebase } from "../../context/FirebaseContext";
import ExternalGuides from "../ExternalGuides/ExternalGuides";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FolderIcon from "@mui/icons-material/Folder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const Projects = ({
	setShowProjects,
	selectedProject,
	setSelectedProject,
	getProjectsFB,
	getProject,
	onCreatedNewGuide,
}) => {
	const [projects, setProjects] = useState([]);
	const [loadingProjects, setLoadingProjects] = useState(false);
	const [loadingNew, setLoadingNew] = useState(false);
	const [value, setValue] = useState(0);

	const {
		user,
		readProjects,
		readPublicProjects,
		createProject,
		deleteProject,
		createPage,
		updateProject,
		setCurrentProject,
		canCreateGuide,
		purchaseSubscription,
	} = useFirebase();

	const [showPurchaseModal, setShowPurchaseModal] = useState(false);

	const getProjects = useCallback(
		async (tab) => {
			if (user) {
				setLoadingProjects(true);
				const userProjects =
					tab === 0 ? await readProjects() : await readPublicProjects();
				console.log(userProjects);
				setProjects(userProjects);
				setLoadingProjects(false);
			}
		},
		[user, readProjects, readPublicProjects]
	);

	useEffect(() => {
		getProjects(value);
	}, [getProjects, value]);

	useEffect(() => {
		console.log(projects);
	}, [projects]);

	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
	const [makePublicConfirmationOpen, setMakePublicConfirmationOpen] =
		useState(false);
	const [projectToDelete, setProjectToDelete] = useState(null);
	const [projectToMakePublic, setProjectToMakePublic] = useState(null);

	const handleTitleBlur = async (project) => {
		await updateProject(
			project.id,
			project.name,
			project.backgroundColor,
			project.textColor,
			project.headerBackgroundColor,
			project.headerTextColor,
			project.fontFamily,
			project.public
		);
		setSelectedProject((selectedProject) => {
			return { ...selectedProject, name: project.name };
		});
	};

	const handleDeleteProject = async () => {
		if (projectToDelete) {
			await deleteProject(projectToDelete.id);
			setProjectToDelete(null);
			setDeleteConfirmationOpen(false);
			await getProjects(0);
			await getProjectsFB(true);
			if (selectedProject.id === projectToDelete.id) {
				setCurrentProject(0);
				setSelectedProject(0);
			}
		}
	};

	const handleMakePublicProject = async () => {
		if (projectToMakePublic) {
			await updateProject(
				projectToMakePublic.id,
				projectToMakePublic.name,
				projectToMakePublic.backgroundColor,
				projectToMakePublic.textColor,
				projectToMakePublic.headerBackgroundColor,
				projectToMakePublic.headerTextColor,
				projectToMakePublic.fontFamily,
				!projectToMakePublic.public
			);
			setProjectToMakePublic(null);
			setMakePublicConfirmationOpen(false);
			await getProjects(0);
		}
	};

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	return (
		<Box sx={{ padding: { xs: 3, sm: 4 } }}>
			<Box sx={{ marginBottom: 4, textAlign: "center" }}>
				<Typography
					component="h1"
					variant="h4"
					sx={{
						fontWeight: 700,
						background:
							"linear-gradient(135deg, rgb(47, 127, 224) 0%, rgb(37, 117, 214) 100%)",
						backgroundClip: "text",
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
						mb: 1,
						fontSize: { xs: "1.75rem", sm: "2rem" },
						letterSpacing: "-0.02em",
					}}
				>
					Tus Guías
				</Typography>
				<Typography
					variant="body2"
					sx={{ 
						color: "text.secondary", 
						fontSize: { xs: "0.875rem", sm: "0.938rem" },
						lineHeight: 1.6,
					}}
				>
					Gestiona y organiza tus guías de viaje
				</Typography>
			</Box>
			<Tabs
				value={value}
				onChange={handleChange}
				aria-label="projects"
				sx={{
					mb: 3,
					"& .MuiTabs-indicator": {
						backgroundColor: "rgb(47, 127, 224)",
						height: 3,
						borderRadius: "3px 3px 0 0",
					},
					"& .MuiTab-root": {
						textTransform: "none",
						fontWeight: 600,
						fontSize: "0.95rem",
						minHeight: 48,
						color: "text.secondary",
						"&:hover": {
							color: "rgb(47, 127, 224)",
						},
					},
					"& .Mui-selected": {
						color: "rgb(47, 127, 224) !important",
						fontWeight: 700,
					},
				}}
			>
				<Tab label="Tus proyectos" {...a11yProps(0)} />
				<Tab label="Guías externas" {...a11yProps(1)} />
			</Tabs>
			{value === 0 ? (
				<Box>
					{loadingProjects ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								padding: 4,
							}}
						>
							<CircularProgress size={32} sx={{ color: "rgb(47, 127, 224)" }} />
						</Box>
					) : (
						<Grid container spacing={3} sx={{ marginBottom: 4 }}>
							{projects.map((project, index) => (
								<Grid item xs={12} sm={6} md={4} key={project.id}>
									<Card
										sx={{
											height: "100%",
											display: "flex",
											flexDirection: "column",
											border:
												selectedProject?.id === project.id
													? "2px solid rgb(47, 127, 224)"
													: "1px solid rgba(15, 23, 42, 0.08)",
											borderRadius: 3,
											boxShadow:
												selectedProject?.id === project.id
													? "0 8px 24px rgba(47, 127, 224, 0.2)"
													: "0 1px 3px rgba(0,0,0,0.08)",
											transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
											position: "relative",
											overflow: "hidden",
											backgroundColor: "#ffffff",
											"&:hover": {
												transform: "translateY(-8px)",
												boxShadow: "0 12px 32px rgba(47, 127, 224, 0.15)",
												borderColor:
													selectedProject?.id === project.id
														? "rgb(47, 127, 224)"
														: "rgba(47, 127, 224, 0.3)",
											},
										}}
									>
										{/* Header con gradiente de color */}
										<Box
											sx={{
												height: 120,
												background: project.headerBackgroundColor
													? `linear-gradient(135deg, ${project.headerBackgroundColor} 0%, ${project.headerBackgroundColor}dd 100%)`
													: "linear-gradient(135deg, rgb(47, 127, 224) 0%, rgb(37, 117, 214) 100%)",
												position: "relative",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												overflow: "hidden",
											}}
										>
											{/* Patrón decorativo */}
											<Box
												sx={{
													position: "absolute",
													width: "200%",
													height: "200%",
													background:
														"radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)",
													backgroundSize: "30px 30px",
													opacity: 0.3,
													animation: "float 20s infinite linear",
													"@keyframes float": {
														"0%": { transform: "translate(0, 0)" },
														"100%": { transform: "translate(30px, 30px)" },
													},
												}}
											/>
											{/* Badge de número */}
											<Chip
												label={`#${index + 1}`}
												size="small"
												sx={{
													position: "absolute",
													top: 12,
													right: 12,
													backgroundColor: "rgba(255,255,255,0.95)",
													color:
														project.headerBackgroundColor ||
														"rgb(47, 127, 224)",
													fontWeight: 700,
													fontSize: "0.75rem",
													height: 26,
													boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
													zIndex: 1,
												}}
											/>
											{/* Icono o inicial del proyecto */}
											<Box
												sx={{
													width: 64,
													height: 64,
													borderRadius: "50%",
													backgroundColor: "rgba(255,255,255,0.2)",
													backdropFilter: "blur(10px)",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "2rem",
													fontWeight: 700,
													color: "#fff",
													textShadow: "0 2px 4px rgba(0,0,0,0.2)",
													border: "2px solid rgba(255,255,255,0.3)",
												}}
											>
												{project.name?.charAt(0)?.toUpperCase() || "G"}
											</Box>
										</Box>
										<CardContent
											sx={{
												flex: 1,
												display: "flex",
												flexDirection: "column",
												padding: 3,
												gap: 1.5,
												"&:last-child": { paddingBottom: 3 },
											}}
										>
											{/* Título editable */}
											<Typography
												component="h2"
												variant="h6"
												contentEditable
												onInput={(e) => {
													setProjects((prev) => {
														prev[index].name = e.target.textContent;
														return prev;
													});
												}}
												onBlur={() => handleTitleBlur(project)}
												sx={{
													fontWeight: 700,
													color: "#0f172a",
													minHeight: 40,
													fontSize: "1.125rem",
													lineHeight: 1.3,
													letterSpacing: "-0.01em",
													"&:focus": {
														outline: "2px solid rgb(47, 127, 224)",
														outlineOffset: 4,
														borderRadius: 1,
													},
												}}
											>
												{project.name}
											</Typography>

											{/* Fecha */}
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													mb: 2,
													color: "text.secondary",
												}}
											>
												<CalendarTodayIcon sx={{ fontSize: 16 }} />
												<Typography
													variant="body2"
													sx={{ fontSize: "0.85rem" }}
												>
													{Intl.DateTimeFormat("es", {
														day: "2-digit",
														month: "short",
														year: "numeric",
													}).format(project.createdAt)}
												</Typography>
											</Box>

											{/* Badge de estado */}
											{selectedProject?.id === project.id ? (
												<Chip
													icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
													label="Editando"
													size="small"
													sx={{
														backgroundColor: "rgb(47, 127, 224)",
														color: "#fff",
														fontWeight: 600,
														mb: 2,
														"& .MuiChip-icon": {
															color: "#fff",
														},
													}}
												/>
											) : (
												<Box sx={{ height: 32, mb: 2 }} />
											)}

											{/* Spacer */}
											<Box sx={{ flexGrow: 1 }} />

											{/* Botones de acción */}
											<Box
												sx={{
													display: "flex",
													gap: 1.5,
													mt: 2,
													pt: 2.5,
													borderTop: "1px solid rgba(15, 23, 42, 0.08)",
												}}
											>
												<Button
													startIcon={<EditIcon sx={{ fontSize: 18 }} />}
													variant="contained"
													size="medium"
													onClick={async () => {
														setCurrentProject(index);
														setSelectedProject(project);
														await getProjectsFB(true);
														setShowProjects(false);
													}}
													sx={{
														flex: 1,
														backgroundColor: "rgb(47, 127, 224)",
														color: "#fff",
														textTransform: "none",
														fontWeight: 600,
														fontSize: "0.875rem",
														borderRadius: 2,
														py: 1.25,
														boxShadow: "0 2px 8px rgba(47, 127, 224, 0.2)",
														transition: "all 0.2s ease",
														"&:hover": {
															backgroundColor: "rgb(37, 117, 214)",
															boxShadow: "0 4px 12px rgba(47, 127, 224, 0.3)",
															transform: "translateY(-1px)",
														},
													}}
												>
													Editar
												</Button>
												<Button
													startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
													variant="outlined"
													size="medium"
													onClick={() => {
														setProjectToDelete(project);
														setDeleteConfirmationOpen(true);
													}}
													sx={{
														flex: 1,
														borderColor: "rgba(239,68,68,0.4)",
														color: "#ef4444",
														textTransform: "none",
														fontWeight: 600,
														fontSize: "0.875rem",
														borderRadius: 2,
														py: 1.25,
														transition: "all 0.2s ease",
														"&:hover": {
															borderColor: "#dc2626",
															backgroundColor: "rgba(239,68,68,0.08)",
															transform: "translateY(-1px)",
															boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
														},
													}}
												>
													Borrar
												</Button>
											</Box>
										</CardContent>
									</Card>
								</Grid>
							))}
						</Grid>
					)}
				<Button
					fullWidth
					variant="contained"
					startIcon={
						loadingNew ? (
							<CircularProgress size={20} color="inherit" />
						) : (
							<AddIcon />
						)
					}
					disabled={loadingNew}
					onClick={async () => {
						const createCheck = canCreateGuide();
						if (!createCheck.allowed) {
							setShowPurchaseModal(true);
							return;
						}
						setLoadingNew(true);
						const docRef = await createProject("Nueva guía");
						if (!docRef) {
							setLoadingNew(false);
							return;
						}
						await createPage(docRef.id, {
							templateId: 1,
							images: [null, null, null],
							texts: [
								{ content: "", isEditing: false },
								{ content: "", isEditing: false },
								{ content: "", isEditing: false },
							],
						});
						setLoadingNew(false);
						if (onCreatedNewGuide) {
							onCreatedNewGuide(docRef.id, docRef.userId);
						} else {
							getProjects(0);
						}
					}}
					sx={{
						py: 1.75,
						fontWeight: 600,
						borderRadius: 3,
						textTransform: "none",
						fontSize: "0.938rem",
						backgroundColor: "rgb(47, 127, 224)",
						boxShadow: "0 4px 14px rgba(47, 127, 224, 0.3)",
						transition: "all 0.2s ease",
						"&:hover": {
							backgroundColor: "rgb(37, 117, 214)",
							boxShadow: "0 6px 18px rgba(47, 127, 224, 0.4)",
							transform: "translateY(-2px)",
						},
					}}
				>
					{loadingNew ? "Creando..." : "Crear nueva guía"}
				</Button>
				<PurchaseChoiceModal
					open={showPurchaseModal}
					guide={showPurchaseModal ? { id: "new", name: "Nueva guía" } : null}
					onClose={() => setShowPurchaseModal(false)}
					onBuyGuide={() => setShowPurchaseModal(false)}
					onSubscribe={async () => {
						try {
							await purchaseSubscription();
						} catch (error) {
							console.error("Error al iniciar la suscripción:", error);
							setShowPurchaseModal(false);
							throw error;
						}
					}}
					limitInfo={{
						currentGuides: canCreateGuide().currentGuides ?? projects?.length ?? 0,
						maxGuides: canCreateGuide().maxGuides ?? 1,
					}}
				/>
				</Box>
			) : (
				<Box>
					{loadingProjects ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								marginBottom: 2,
							}}
						>
							<CircularProgress size={24} sx={{ color: "#56BB90" }} />
						</Box>
					) : (
						<Grid container spacing={3} sx={{ marginBottom: 2 }}>
							{projects.map((project, index) => (
								<Grid item xs={12} sm={6} md={4} key={project.id}>
									<Card
										sx={{
											height: "100%",
											display: "flex",
											flexDirection: "column",
											border: "1px solid rgba(47, 127, 224,0.2)",
											borderRadius: 3,
											boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
											transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
											position: "relative",
											overflow: "hidden",
											"&:hover": {
												transform: "translateY(-6px)",
												boxShadow: "0 16px 40px rgba(47, 127, 224,0.25)",
												borderColor: "rgb(47, 127, 224)",
											},
										}}
									>
										{/* Header con gradiente */}
										<Box
											sx={{
												height: 120,
												background: project.headerBackgroundColor
													? `linear-gradient(135deg, ${project.headerBackgroundColor} 0%, ${project.headerBackgroundColor}dd 100%)`
													: "linear-gradient(135deg, rgba(47, 127, 224, 0.8) 0%, rgba(37, 117, 214, 0.8) 100%)",
												position: "relative",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												overflow: "hidden",
											}}
										>
											<Box
												sx={{
													position: "absolute",
													width: "200%",
													height: "200%",
													background:
														"radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)",
													backgroundSize: "30px 30px",
													opacity: 0.3,
												}}
											/>
											<Box
												sx={{
													width: 64,
													height: 64,
													borderRadius: "50%",
													backgroundColor: "rgba(255,255,255,0.2)",
													backdropFilter: "blur(10px)",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "2rem",
													fontWeight: 700,
													color: "#fff",
													textShadow: "0 2px 4px rgba(0,0,0,0.2)",
													border: "2px solid rgba(255,255,255,0.3)",
												}}
											>
												{project.name?.charAt(0)?.toUpperCase() || "G"}
											</Box>
										</Box>
										<CardContent
											sx={{
												flex: 1,
												display: "flex",
												flexDirection: "column",
												padding: 2.5,
												"&:last-child": { paddingBottom: 2.5 },
											}}
										>
											<Typography
												component="h2"
												variant="h6"
												onInput={(e) => {
													setProjects((prev) => {
														prev[index].name = e.target.textContent;
														return prev;
													});
												}}
												onBlur={() => handleTitleBlur(project)}
												contentEditable
												sx={{
													fontWeight: 700,
													color: "#1e293b",
													mb: 1.5,
													minHeight: 32,
													fontSize: "1.15rem",
													"&:focus": {
														outline: "2px solid rgb(47, 127, 224)",
														outlineOffset: 2,
														borderRadius: 1,
													},
												}}
											>
												{project.name}
											</Typography>

											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													mb: 2,
													color: "text.secondary",
												}}
											>
												<CalendarTodayIcon sx={{ fontSize: 16 }} />
												<Typography
													variant="body2"
													sx={{ fontSize: "0.85rem" }}
												>
													{Intl.DateTimeFormat("es", {
														day: "2-digit",
														month: "short",
														year: "numeric",
													}).format(project.createdAt)}
												</Typography>
											</Box>

											<Box
												sx={{
													mt: "auto",
													pt: 2,
													borderTop: "1px solid rgba(148,163,184,0.1)",
												}}
											>
												<Button
													fullWidth
													startIcon={<ContentCopyIcon />}
													variant="outlined"
													size="small"
													onClick={async () => {
														setSelectedProject(project);
														await getProject(project.userId, project.id);
														setShowProjects(false);
													}}
													sx={{
														borderColor: "rgb(47, 127, 224)",
														color: "rgb(47, 127, 224)",
														textTransform: "none",
														fontWeight: 600,
														borderRadius: 2,
														py: 1.25,
														transition: "all 0.2s ease",
														"&:hover": {
															borderColor: "rgb(37, 117, 214)",
															backgroundColor: "rgba(47, 127, 224,0.08)",
															transform: "translateY(-1px)",
															boxShadow: "0 4px 12px rgba(47, 127, 224,0.2)",
														},
													}}
												>
													Usar como plantilla
												</Button>
											</Box>
										</CardContent>
									</Card>
								</Grid>
							))}
						</Grid>
					)}
				</Box>
			)}
			{value === 1 && <ExternalGuides />}

			<BasicModal
				width={500}
				showModal={deleteConfirmationOpen}
				handleClose={() => {
					setDeleteConfirmationOpen(false);
				}}
				children={
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							textAlign: "center",
							padding: 1,
						}}
					>
						<Box
							sx={{
								width: 64,
								height: 64,
								borderRadius: "50%",
								backgroundColor: "rgba(239,68,68,0.1)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								margin: "0 auto 2rem",
							}}
						>
							<DeleteIcon sx={{ fontSize: 32, color: "#ef4444" }} />
						</Box>
						<Typography
							component="h2"
							variant="h5"
							sx={{
								fontWeight: 700,
								marginBottom: 1.5,
								color: "#1e293b",
							}}
						>
							¿Eliminar esta guía?
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: "text.secondary",
								marginBottom: 3,
								lineHeight: 1.6,
							}}
						>
							Esta acción no se puede deshacer. Perderás todo el contenido de la
							guía permanentemente.
						</Typography>
						<Box
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "flex-end",
							}}
						>
							<Button
								variant="outlined"
								onClick={() => setDeleteConfirmationOpen(false)}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									px: 3,
								}}
							>
								Cancelar
							</Button>
							<Button
								variant="contained"
								startIcon={<DeleteIcon />}
								onClick={handleDeleteProject}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									px: 3,
									backgroundColor: "#ef4444",
									boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
									"&:hover": {
										backgroundColor: "#dc2626",
										boxShadow: "0 4px 12px rgba(239,68,68,0.4)",
									},
								}}
							>
								Eliminar
							</Button>
						</Box>
					</Box>
				}
			/>
			<BasicModal
				width={500}
				showModal={makePublicConfirmationOpen}
				handleClose={() => {
					setMakePublicConfirmationOpen(false);
				}}
				children={
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							textAlign: "center",
							padding: 1,
						}}
					>
						<Typography
							component="h2"
							variant="h5"
							sx={{
								fontWeight: 700,
								marginBottom: 1.5,
								color: "#1e293b",
							}}
						>
							{selectedProject?.public
								? "¿Hacer privada esta guía?"
								: "¿Hacer pública esta guía?"}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: "text.secondary",
								marginBottom: 3,
								lineHeight: 1.6,
							}}
						>
							{selectedProject?.public
								? "Solo tú podrás ver y editar esta guía."
								: "Todos los usuarios podrán ver esta guía y usarla como plantilla para crear sus propias guías."}
						</Typography>
						<Box
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "flex-end",
							}}
						>
							<Button
								variant="outlined"
								onClick={() => setMakePublicConfirmationOpen(false)}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									px: 3,
								}}
							>
								Cancelar
							</Button>
							<Button
								variant="contained"
								onClick={handleMakePublicProject}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									px: 3,
									backgroundColor: "rgb(47, 127, 224)",
									boxShadow: "0 2px 8px rgba(47, 127, 224,0.3)",
									"&:hover": {
										backgroundColor: "rgb(47, 127, 224)",
										boxShadow: "0 4px 12px rgba(47, 127, 224,0.4)",
									},
								}}
							>
								{selectedProject?.public ? "Hacer privada" : "Hacer pública"}
							</Button>
						</Box>
					</Box>
				}
			/>
		</Box>
	);
};

export default Projects;
